/**
 * Central GTM dataLayer tracking utility.
 * All events are pushed to window.dataLayer for GTM processing.
 * No direct GA4/gtag calls — GTM handles all tag firing.
 */

import { getNis2EntrySource, setNis2EntrySource, type Nis2EntrySource } from '@/lib/nis2/entrySource';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

// Dedup guard: prevent duplicate pushes within 2s (React strict mode, re-renders)
const _recentPushes = new Map<string, number>();
const DEDUP_WINDOW_MS = 2000;

function isDuplicate(key: string): boolean {
  const now = Date.now();
  const last = _recentPushes.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) return true;
  _recentPushes.set(key, now);
  // Cleanup old entries periodically
  if (_recentPushes.size > 50) {
    for (const [k, v] of _recentPushes) {
      if (now - v > DEDUP_WINDOW_MS * 2) _recentPushes.delete(k);
    }
  }
  return false;
}

/** Core push — auto-includes page_path + page_title */
export function trackEvent(
  event: string,
  params?: Record<string, unknown>,
  dedupeKey?: string,
) {
  if (typeof window === 'undefined') return;
  if (dedupeKey && isDuplicate(dedupeKey)) return;

  const payload = {
    event,
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
    ...params,
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);

  // DEV-only logging for tracking validation
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('Tracking event fired:', event, payload);
  }
}

// ─── Wizard events ──────────────────────────────────────────────

/**
 * Optional structured context shared across wizard events.
 * All fields are optional — pass whatever is known at the call site.
 */
export interface WizardEventContext {
  selected_services?: string[];
  selected_region?: string;
  matched_msp_count?: number;
  contact_method?: 'top3_multi' | 'single_msp' | 'callback' | 'message' | 'quote';
}

function cleanCtx(ctx?: WizardEventContext): Record<string, unknown> {
  if (!ctx) return {};
  const out: Record<string, unknown> = {};
  if (ctx.selected_services && ctx.selected_services.length) out.selected_services = ctx.selected_services;
  if (ctx.selected_region) out.selected_region = ctx.selected_region;
  if (typeof ctx.matched_msp_count === 'number') out.matched_msp_count = ctx.matched_msp_count;
  if (ctx.contact_method) out.contact_method = ctx.contact_method;
  return out;
}

export function trackWizardLandingView() {
  trackEvent('wizard_landing_view', undefined, 'wizard_landing_view');
}

export function trackWizardTypeSelect(wizard_type: string) {
  trackEvent('wizard_type_select', { wizard_type });
}

export function trackWizardStepView(wizard_type: string, step_number: number, step_name: string, ctx?: WizardEventContext) {
  trackEvent(
    'wizard_step_view',
    { wizard_type, wizard_step_number: step_number, step_number, step_name, ...cleanCtx(ctx) },
    `wizard_step_view_${wizard_type}_${step_number}`,
  );
}

export function trackWizardStepComplete(wizard_type: string, step_number: number, step_name: string, answers_count: number, ctx?: WizardEventContext) {
  trackEvent('wizard_step_complete', {
    wizard_type,
    wizard_step_number: step_number,
    step_number,
    step_name,
    answers_count,
    ...cleanCtx(ctx),
  });
}

export function trackWizardResultsView(wizard_type: string, result_count: number, top_n_shown: number, ctx?: WizardEventContext) {
  trackEvent(
    'wizard_results_view',
    { wizard_type, result_count, matched_msp_count: result_count, top_n_shown, ...cleanCtx(ctx) },
    `wizard_results_view_${wizard_type}`,
  );
}

export function trackWizardTop3ContactClick(wizard_type: string, selected_msp_count: number, ctx?: WizardEventContext) {
  trackEvent('wizard_top3_contact_click', {
    wizard_type,
    selected_msp_count,
    contact_method: 'top3_multi',
    ...cleanCtx(ctx),
  });
}

export function trackWizardSingleMspContactClick(wizard_type: string, msp_id: string, msp_name: string, list_position: number, ctx?: WizardEventContext) {
  trackEvent('wizard_single_msp_contact_click', {
    wizard_type,
    msp_id,
    msp_name,
    list_position,
    source_list: 'wizard_results',
    contact_method: 'single_msp',
    ...cleanCtx(ctx),
  });
}

export function trackWizardProfileClick(wizard_type: string, msp_id: string, msp_name: string, list_position: number, ctx?: WizardEventContext) {
  trackEvent('wizard_profile_click', {
    wizard_type,
    msp_id,
    msp_name,
    list_position,
    source_list: 'wizard_results',
    ...cleanCtx(ctx),
  });
}

export function trackWizardShortlistCtaClick(wizard_type: string, matched_msp_count: number, ctx?: WizardEventContext) {
  trackEvent('wizard_shortlist_cta_click', {
    wizard_type,
    matched_msp_count,
    source_list: 'wizard_results_after_top3',
    ...cleanCtx(ctx),
  });
}

// ─── Lead form events ───────────────────────────────────────────

export type LeadType = 'top3_multi' | 'single_msp' | 'profile_callback' | 'profile_message' | 'profile_quote' | 'compare_multi' | 'compare_single';

export function trackLeadFormView(lead_type: LeadType, selected_msp_count: number, msp_id?: string, msp_name?: string, source_list?: string) {
  trackEvent('lead_form_view', { lead_type, selected_msp_count, ...(msp_id && { msp_id }), ...(msp_name && { msp_name }), ...(source_list && { source_list }) });
}

export function trackLeadFormSubmit(lead_type: LeadType, selected_msp_count: number, msp_id?: string, msp_name?: string) {
  trackEvent('lead_form_submit', { lead_type, selected_msp_count, ...(msp_id && { msp_id }), ...(msp_name && { msp_name }) });
}

/** Fires immediately on submit click, BEFORE validation — measures raw submit intent */
export function trackLeadFormSubmitAttempt(lead_type: LeadType, source: string, selected_msp_count?: number) {
  trackEvent('lead_form_submit_attempt', {
    lead_type,
    source,
    ...(typeof selected_msp_count === 'number' && { selected_msp_count }),
  });
}

/** Fires per failing field when client-side validation blocks submission */
export function trackLeadFormValidationError(lead_type: LeadType, field: string, source: string) {
  trackEvent('lead_form_validation_error', { lead_type, field, source });
}

export type SubmissionType = 'real' | 'test';

export function trackLeadFormSubmitSuccess(
  lead_type: LeadType,
  selected_msp_count: number,
  msp_id?: string,
  msp_name?: string,
  submission_type: SubmissionType = 'real',
) {
  trackEvent('lead_form_submit_success', {
    lead_type,
    selected_msp_count,
    submission_type,
    ...(msp_id && { msp_id }),
    ...(msp_name && { msp_name }),
  });
}

export function trackLeadFormSubmitError(
  lead_type: LeadType,
  error_type: string,
  extra?: { source?: string; requested_msp_count?: number; success_count?: number; failure_count?: number },
) {
  trackEvent('lead_form_submit_error', {
    lead_type,
    error_type,
    ...(extra?.source && { source: extra.source }),
    ...(typeof extra?.requested_msp_count === 'number' && { requested_msp_count: extra.requested_msp_count }),
    ...(typeof extra?.success_count === 'number' && { success_count: extra.success_count }),
    ...(typeof extra?.failure_count === 'number' && { failure_count: extra.failure_count }),
  });
}

/**
 * Multi-MSP partial success: 0 < success_count < requested_msp_count.
 * Uitsluitend analytisch — NIET als primaire Google Ads-conversie gebruiken.
 * Géén submission_group_id of andere identifier in deze payload.
 */
export function trackLeadFormSubmitPartial(
  lead_type: LeadType,
  source: string,
  requested_msp_count: number,
  success_count: number,
  failure_count: number,
  submission_type: SubmissionType = 'real',
) {
  trackEvent('lead_form_submit_partial', {
    lead_type,
    source,
    requested_msp_count,
    success_count,
    failure_count,
    submission_type,
  });
}

/**
 * Duplicaat-inzending: de server bevestigde dat het leadrecord al bestond
 * (24u-dedup of idempotency-hit) en heeft GEEN nieuwe lead aangemaakt.
 * Uitsluitend analytisch — nooit als Google Ads-conversie gebruiken.
 */
export function trackLeadFormSubmitDuplicate(
  lead_type: LeadType,
  source: string,
  requested_msp_count: number,
  duplicate_count: number,
  submission_type: SubmissionType = 'real',
) {
  trackEvent('lead_form_submit_duplicate', {
    lead_type,
    source,
    requested_msp_count,
    duplicate_count,
    submission_type,
  });
}

/**
 * Beslislogica voor één zichtbare submitactie (1..N ontvangers).
 *
 *  - minimaal één NIEUWE lead        -> 'success'   (exact één conversie)
 *  - nieuw/duplicaat + mislukkingen  -> 'partial'   (analytisch, geen conversie)
 *  - 0 nieuw, minimaal één duplicaat -> 'duplicate' (analytisch, geen conversie)
 *  - alleen mislukkingen             -> 'error'
 */
export function resolveLeadBatchOutcome(counts: {
  created: number;
  duplicate: number;
  failed: number;
}): 'success' | 'partial' | 'duplicate' | 'error' {
  const { created, duplicate, failed } = counts;
  if (created > 0) return failed > 0 ? 'partial' : 'success';
  if (duplicate > 0) return failed > 0 ? 'partial' : 'duplicate';
  return 'error';
}



// ─── MSP profile events ────────────────────────────────────────

export function trackMspProfileView(msp_id: string, msp_name: string, source_list?: string) {
  trackEvent('msp_profile_view', { msp_id, msp_name, source_list: source_list || 'direct' }, `msp_profile_view_${msp_id}`);
}

export function trackProfileContactClick(contact_type: 'callback' | 'message' | 'quote', msp_id: string, msp_name: string) {
  trackEvent('profile_contact_click', { contact_type, msp_id, msp_name, source_list: 'profile_page' });
}

export function trackProfileWebsiteClick(msp_id: string, msp_name: string) {
  trackEvent('profile_website_click', { msp_id, msp_name, source_list: 'profile_page' });
}

// ─── Directory / Compare events ─────────────────────────────────

export function trackDirectoryView() {
  trackEvent('directory_view', undefined, 'directory_view');
}

export function trackDirectoryFilterApply(filter_name: string, filter_value: string, results_count: number) {
  trackEvent('directory_filter_apply', { filter_name, filter_value, results_count });
}

export function trackDirectoryCompareSelect(msp_id: string, msp_name: string, selected_count: number) {
  trackEvent('directory_compare_select', { msp_id, msp_name, selected_count, source_list: 'directory' });
}

export function trackDirectoryCompareView(selected_msp_count: number) {
  trackEvent('directory_compare_view', { selected_msp_count }, 'directory_compare_view');
}

export function trackDirectoryMspContactClick(msp_id: string, msp_name: string) {
  trackEvent('directory_msp_contact_click', { msp_id, msp_name, source_list: 'compare_page' });
}

export function trackDirectoryCompareContactClick(selected_msp_count: number) {
  trackEvent('directory_compare_contact_click', { selected_msp_count, source_list: 'compare_page' });
}

export function trackDirectoryProfileClick(msp_id: string, msp_name: string, list_name: string, list_position?: number) {
  trackEvent('directory_profile_click', { msp_id, msp_name, source_list: list_name, ...(list_position !== undefined && { list_position }) });
}

// ─── CTA events ─────────────────────────────────────────────────

export function trackCtaClick(cta_name: string, cta_location: string, destination: string) {
  trackEvent('cta_click', { cta_name, cta_location, destination });
}

// ─── AI-match events ────────────────────────────────────────────
// Technische events, GEEN leadconversies. Nooit invoertekst, e-mail,
// bedrijfsnaam, IP of andere PII meesturen — alleen onderstaande parameters.

export type AiMatchEvent =
  | 'ai_match_submit'
  | 'ai_match_success'
  | 'ai_match_no_result'
  | 'ai_match_error'
  | 'ai_match_rate_limited'
  | 'ai_match_profile_click'
  | 'ai_match_wizard_click'
  // AI-match v2 — uitsluitend technische statussen en aantallen; geen leadconversies.
  | 'ai_match_suggest_shown'
  | 'ai_match_clarify_answered'
  | 'ai_match_confirmed'
  | 'ai_match_dont_know'
  | 'ai_match_results_shown'
  // Homepagekaart — alleen technisch; geen leadconversie, nooit inhoud.
  | 'ai_match_home_view'
  | 'ai_match_home_start'
  | 'ai_match_home_continue';

export interface AiMatchEventParams {
  gateway_ok?: boolean;
  ai_accepted?: boolean;
  suggestion_count?: number;
  clarification_shown?: boolean;
  answer_type?: 'service_option' | 'dont_know';
  confirmed_count?: number;
  removed_count?: number;
  added_count?: number;
  match_count?: number;
  resultaat_count?: number;
  duurklasse?: 'lt3s' | '3_10s' | '10_30s' | 'gt30s';
  error_type?: string;
  bestemming?: 'profiel' | 'wizard';
  mode?: 'ai' | 'fallback';
  list_position?: number;
}

const AI_MATCH_ALLOWED_KEYS = [
  'resultaat_count', 'duurklasse', 'error_type', 'bestemming', 'mode', 'list_position',
  'gateway_ok', 'ai_accepted', 'suggestion_count', 'clarification_shown', 'answer_type',
  'confirmed_count', 'removed_count', 'added_count', 'match_count',
] as const;

export function aiMatchDurationClass(ms: number): NonNullable<AiMatchEventParams['duurklasse']> {
  if (ms < 3000) return 'lt3s';
  if (ms < 10000) return '3_10s';
  if (ms < 30000) return '10_30s';
  return 'gt30s';
}

export function trackAiMatch(event: AiMatchEvent, params: AiMatchEventParams = {}) {
  const safe: Record<string, unknown> = {};
  for (const k of AI_MATCH_ALLOWED_KEYS) {
    const v = params[k];
    if (typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && /^[a-z0-9_]{1,30}$/.test(v))) safe[k] = v;
  }
  trackEvent(event, safe);
}


// ─── NIS2 Zelfscan events ───────────────────────────────────────────────
// Phase 1: none of these is a primary Google Ads conversion.
// Never send email, phone, company name, exact financial data or full answers.

export interface Nis2ScanEventContext {
  scan_version: string;
  step_number?: number;
  sector_group?: string;
  size_class?: string;
  scope_outcome?: string;
  scope_certainty?: string;
  maturity_label?: string;
  gap_count?: number;
  is_test?: boolean;
}

function cleanNis2Ctx(ctx: Nis2ScanEventContext): Record<string, unknown> {
  const out: Record<string, unknown> = { scan_version: ctx.scan_version, entry_source: getNis2EntrySource() };
  if (typeof ctx.step_number === 'number') out.step_number = ctx.step_number;
  if (ctx.sector_group) out.sector_group = ctx.sector_group;
  if (ctx.size_class) out.size_class = ctx.size_class;
  if (ctx.scope_outcome) out.scope_outcome = ctx.scope_outcome;
  if (ctx.scope_certainty) out.scope_certainty = ctx.scope_certainty;
  if (ctx.maturity_label) out.maturity_label = ctx.maturity_label;
  if (typeof ctx.gap_count === 'number') out.gap_count = ctx.gap_count;
  out.is_test = ctx.is_test === true;
  return out;
}

export function trackNis2ScanView(ctx: Nis2ScanEventContext) {
  trackEvent('nis2_scan_view', cleanNis2Ctx(ctx), 'nis2_scan_view');
}

export function trackNis2ScanStart(ctx: Nis2ScanEventContext) {
  trackEvent('nis2_scan_start', cleanNis2Ctx(ctx), 'nis2_scan_start');
}

export function trackNis2ScanStepComplete(ctx: Nis2ScanEventContext) {
  trackEvent('nis2_scan_step_complete', cleanNis2Ctx(ctx), `nis2_scan_step_complete_${ctx.step_number}`);
}

export function trackNis2FinancialQuestionShown(ctx: Nis2ScanEventContext) {
  trackEvent('nis2_scan_financial_question_shown', cleanNis2Ctx(ctx), 'nis2_scan_financial_question_shown');
}

/** Fires only after the server confirmed a completed, server-recomputed scan. */
export function trackNis2ScanResult(ctx: Nis2ScanEventContext) {
  trackEvent('nis2_scan_result', cleanNis2Ctx(ctx), 'nis2_scan_result');
}

/**
 * NIS2 report-by-email events (phase 2).
 * Never a primary Google Ads conversion. Never carries email, name, company,
 * edit token, scan id, request id, answers or exact results.
 */
export interface Nis2ReportEventContext {
  scan_version: string;
  scope_outcome?: string;
  maturity_label?: string;
  is_test?: boolean;
  error_code?: string;
}

function cleanNis2ReportCtx(ctx: Nis2ReportEventContext): Record<string, unknown> {
  const out: Record<string, unknown> = { scan_version: ctx.scan_version, entry_source: getNis2EntrySource() };
  if (ctx.scope_outcome) out.scope_outcome = ctx.scope_outcome;
  if (ctx.maturity_label) out.maturity_label = ctx.maturity_label;
  if (ctx.error_code) out.error_code = ctx.error_code;
  out.is_test = ctx.is_test === true;
  return out;
}

/** Fires once when the report form becomes visible. */
export function trackNis2ReportFormView(ctx: Nis2ReportEventContext, dedupeKey?: string) {
  trackEvent('nis2_report_form_view', cleanNis2ReportCtx(ctx), dedupeKey ?? 'nis2_report_form_view');
}

/** Fires on a valid submit attempt. */
export function trackNis2ReportSubmit(ctx: Nis2ReportEventContext, dedupeKey?: string) {
  trackEvent('nis2_report_submit', cleanNis2ReportCtx(ctx), dedupeKey);
}

/** Fires only after a confirmed sent / already_sent response. */
export function trackNis2ReportSuccess(ctx: Nis2ReportEventContext, dedupeKey?: string) {
  trackEvent('nis2_report_success', cleanNis2ReportCtx(ctx), dedupeKey);
}

/** Fires on a real final error, never on local field validation. */
export function trackNis2ReportFailure(ctx: Nis2ReportEventContext, dedupeKey?: string) {
  trackEvent('nis2_report_failure', cleanNis2ReportCtx(ctx), dedupeKey);
}

/**
 * NIS2 adviesaanvraag events (fase 3).
 *
 * `nis2_advice_submit` is de ENIGE primaire Google Ads-conversie van de NIS2-flow
 * en vuurt uitsluitend nadat de server een echte, niet-test, niet-spam en
 * niet-duplicate aanvraag heeft opgeslagen (`conversion_eligible === true`).
 * Nooit e-mail, telefoon, naam, bedrijfsnaam, scan-id of request-id meesturen.
 */
export interface Nis2AdviceEventContext {
  scan_version: string;
  scope_outcome?: string;
  maturity_label?: string;
  matched_msp_count?: number;
  is_test?: boolean;
  error_code?: string;
}

function cleanNis2AdviceCtx(ctx: Nis2AdviceEventContext): Record<string, unknown> {
  const out: Record<string, unknown> = { scan_version: ctx.scan_version, entry_source: getNis2EntrySource() };
  if (ctx.scope_outcome) out.scope_outcome = ctx.scope_outcome;
  if (ctx.maturity_label) out.maturity_label = ctx.maturity_label;
  if (typeof ctx.matched_msp_count === 'number') out.matched_msp_count = ctx.matched_msp_count;
  if (ctx.error_code) out.error_code = ctx.error_code;
  out.is_test = ctx.is_test === true;
  return out;
}

/** Niet-primair: het adviesformulier is zichtbaar geworden. */
export function trackNis2AdviceFormView(ctx: Nis2AdviceEventContext, dedupeKey?: string) {
  trackEvent('nis2_advice_form_view', cleanNis2AdviceCtx(ctx), dedupeKey ?? 'nis2_advice_form_view');
}

/** Niet-primair: geldige poging tot verzenden. */
export function trackNis2AdviceSubmitAttempt(ctx: Nis2AdviceEventContext, dedupeKey?: string) {
  trackEvent('nis2_advice_submit_attempt', cleanNis2AdviceCtx(ctx), dedupeKey);
}

/** PRIMAIRE conversie. Alleen aanroepen bij conversion_eligible === true. */
export function trackNis2AdviceSubmit(ctx: Nis2AdviceEventContext, dedupeKey?: string) {
  trackEvent('nis2_advice_submit', { ...cleanNis2AdviceCtx(ctx), conversion_type: 'primary' }, dedupeKey);
}

/** Niet-primair: opslag lukte, maar de aanvraag kwalificeert niet als conversie. */
export function trackNis2AdviceSubmitSuppressed(ctx: Nis2AdviceEventContext, dedupeKey?: string) {
  trackEvent('nis2_advice_submit_suppressed', cleanNis2AdviceCtx(ctx), dedupeKey);
}

/** Niet-primair: echte eindfout, nooit bij lokale veldvalidatie. */
export function trackNis2AdviceFailure(ctx: Nis2AdviceEventContext, dedupeKey?: string) {
  trackEvent('nis2_advice_failure', cleanNis2AdviceCtx(ctx), dedupeKey);
}

/**
 * Click op een expliciet instappunt naar de NIS2 Zelfscan: bron vastleggen en
 * één cta_click sturen. Gebruik `setNis2EntrySource` los wanneer de link al een
 * eigen CTA-event heeft (bv. de homepage-hero).
 */
export function trackNis2EntryClick(source: Nis2EntrySource): void {
  setNis2EntrySource(source);
  trackCtaClick('nis2_scan_entry', source, '/nis2-zelfscan');
}
